import type { FundAccountPair, FundAccountSelection } from '../types/dealerFundStatementPeriod.types';
import { productLabel } from './fundStatementLabels';

export const FUND_ACCOUNTS_PER_PAGE = 10;

export interface FundAccountRow extends FundAccountPair {
    key: string;
    productLabel: string;
    selectable: boolean;
}

export interface FundAccountPage<T> {
    rows: T[];
    page: number;
    lastPage: number;
    from: number;
    to: number;
}

export const fundAccountKey = ({ sellingDealerId, productId }: FundAccountSelection): string =>
    `${sellingDealerId}|${productId}`;

const compareText = (a: string | null, b: string | null): number => (a ?? '').localeCompare(b ?? '');

const compareRows = (a: FundAccountRow, b: FundAccountRow): number =>
    compareText(a.dealerCode, b.dealerCode) ||
    compareText(a.productLabel, b.productLabel) ||
    compareText(a.sageAccountCode, b.sageAccountCode) ||
    compareText(a.key, b.key);

export const buildFundAccountRows = (
    pairs: FundAccountPair[],
    productNames: Map<string, string>,
): FundAccountRow[] =>
    pairs
        .map((pair) => ({
            ...pair,
            key: fundAccountKey(pair),
            productLabel: productLabel(pair.productId, productNames, pair.sageAccountName),
            selectable: pair.setCode !== null,
        }))
        .sort(compareRows);

export const filterFundAccounts = (rows: FundAccountRow[], term: string): FundAccountRow[] => {
    const needle = term.trim().toLowerCase();
    if (needle === '') return rows;
    return rows.filter((row) =>
        [row.dealerCode, row.productLabel, row.sageAccountCode].some((field) => field?.toLowerCase().includes(needle)),
    );
};

export const pageOf = <T>(rows: T[], page: number, perPage: number): FundAccountPage<T> => {
    const lastPage = Math.max(1, Math.ceil(rows.length / perPage));
    const current = Math.min(Math.max(1, page), lastPage);
    const start = (current - 1) * perPage;
    const visible = rows.slice(start, start + perPage);
    return {
        rows: visible,
        page: current,
        lastPage,
        from: visible.length === 0 ? 0 : start + 1,
        to: start + visible.length,
    };
};
